// Configuration de base de l'API
const getApiBaseUrl = () => {
  // En production, utiliser l'URL relative (même domaine)
  if ((import.meta as any).env.PROD) {
    return ''; // URL relative pour production
  }

  // En développement, utiliser la variable d'environnement ou localhost:3001 par défaut
  return (import.meta as any).env.VITE_API_URL || 'http://localhost:3001';
};

// Redirection vers login (importé dynamiquement pour éviter les cycles)
let navigateToLogin: (() => void) | null = null;
export const setLoginRedirect = (fn: () => void) => {
  navigateToLogin = fn;
};

const API_BASE_URL = getApiBaseUrl();

// Interface pour les réponses de l'API
interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  /** Message à montrer quand l'opération a réussi mais qu'une suite n'a pas pu se faire. */
  avertissement?: string;
  /** Le registre des visiteurs n'a pas pu suivre une fiche de coworker (08/10/2026). */
  avertissementUpRegistre?: string;
}

// --- Comptes UP-SPOT (24/09/2026) -------------------------------------------

/** Réponse d'une route UP-SPOT, avec les précisions utiles à l'écran en cas de refus. */
interface ReponseUpSpot<T = any> extends ApiResponse<T> {
  /** CONFIRMATION_ADMIN : il faut confirmer ; COMPTE_EXISTANT : un compte existe déjà à cette adresse. */
  code?: 'CONFIRMATION_ADMIN' | 'COMPTE_EXISTANT';
  compteId?: number;
  contactId?: number;
}

interface CompteUpSpot {
  id: number;
  prenom: string;
  nom: string;
  email: string;
  idRole: number;
  role: string;
  departementId: number | null;
  service: string | null;
  statut: 'actif' | 'desactive';
  invitationEnAttente: boolean;
  derniereConnexion: string | null;
  creeLe: string | null;
  droits: { acces: number[]; gestion: number[] };
  invitationEnvoyee?: boolean;
}

interface ReferentielUpSpot {
  roles: { id: number; nom: string }[];
  services: { id: number; nom: string }[];
  categories: {
    id: number;
    nom: string;
    couleur: string | null;
    ressources: { id: number; nom: string; statut: string | null }[];
  }[];
}

interface ResumeCompteUpSpot { id: number; prenom: string; nom: string; email: string }
interface ResumeFicheUpSpot { id: number; prenom: string; nom: string; entreprise: string | null }

interface AccesUpSpotFiche {
  configure: boolean;
  peutGerer: boolean;
  lien: { compteId: number; creeParSynchro: boolean; liePar: string | null; lieLe: string } | null;
  compte: CompteUpSpot | null;
  erreurCompte: string | null;
  porteParAutreFiche: { contactId: number; prenom: string; nom: string } | null;
  compteARattacher: ResumeCompteUpSpot | null;
}

interface EtatSynchroUpSpot {
  configure: boolean;
  lanceeLe: string | null;
  termineeLe: string | null;
  declencheur: string | null;
  rapport: {
    configure: boolean;
    erreur?: string;
    comptes: number;
    relies: number;
    crees: { compte: ResumeCompteUpSpot; contactId: number }[];
    aArbitrer: { compte: ResumeCompteUpSpot; candidats: ResumeFicheUpSpot[]; motif: string }[];
    homonymes: { contactId: number; compte: ResumeCompteUpSpot; autres: ResumeFicheUpSpot[] }[];
    echecs: { compte: ResumeCompteUpSpot; erreur: string }[];
    fichesSupprimees: ResumeCompteUpSpot[];
    liensRetires: number;
  } | null;
  fichesCreees: { contactId: number; prenom: string; nom: string; email: string; creeLe: string }[];
  journal: { faitLe: string; faitPar: string | null; action: string; contactId: number | null; compteId: number | null; details: string }[];
}

// Interface pour l'utilisateur - Structure réelle de la DB
interface User {
  id: string; // VARCHAR dans la vraie DB
  username: string; // username au lieu d'email
  profile_picture?: string; // Photo de profil optionnelle
  is_admin: boolean; // Boolean is_admin au lieu du système de rôles
  created_at: string;
  updated_at: string;
}

// Interface pour la réponse d'authentification
interface AuthResponse {
  user: User;
  token: string;
  expires_at: string;
}

// Interface pour les données de connexion
interface LoginCredentials {
  username: string; // username au lieu d'email
  password: string;
}

// Interface pour les entreprises - Structure réelle de la DB
interface Company {
  id: number; // AUTO_INCREMENT dans la vraie DB companies
  name: string;
  website?: string;
  created_at: string;
  updated_at: string;
  postal_code?: string;
  city?: string;
  street_address?: string;
  address_complement?: string;
}

// Interface pour créer une entreprise
interface CompanyCreateInput {
  name: string;
  website?: string;
  postal_code?: string;
  city?: string;
  street_address?: string;
  address_complement?: string;
}

// Interface pour les valeurs de champs personnalisés des entreprises
interface CompanyCustomValueInput {
  company_custom_field_id: number;
  value: string;
}

// Interface pour créer une entreprise avec champs personnalisés
interface CompanyCreateInputWithCustomFields extends CompanyCreateInput {
  custom_fields?: CompanyCustomValueInput[];
}

// Interface pour une entreprise avec ses champs personnalisés
interface CompanyWithCustomFields extends Company {
  custom_fields?: {
    field: CompanyCustomField & { options?: CompanyCustomFieldOption[] };
    value?: string;
    options?: CompanyCustomFieldOption[];
  }[];
}

// Interface pour les contacts - Structure basée sur votre schéma DB
interface Contact {
  id: number; // AUTO_INCREMENT dans la vraie DB contacts
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  linkedin_url?: string;
  company_id?: number;
  created_at: string;
  updated_at: string;
  // Champs additionnels des jointures
  company_name?: string;
  // Compte UP-SPOT porté par la fiche (état mémorisé, pour la liste)
  upspot_compte_id?: number | null;
  upspot_role?: string | null;
  upspot_statut?: 'actif' | 'desactive' | null;
  upspot_invitation_en_attente?: number | boolean | null;
}

// Interface pour créer un contact
interface ContactCreateInput {
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  linkedin_url?: string;
  company_id?: number;
}

// Interface pour les valeurs de champs personnalisés des contacts
interface ContactCustomValueInput {
  contacts_custom_field_id: number;
  value: string;
}

// Interface pour créer un contact avec champs personnalisés
interface ContactCreateInputWithCustomFields extends ContactCreateInput {
  custom_fields?: ContactCustomValueInput[];
}

// Interface pour un contact avec ses champs personnalisés
interface ContactWithCustomFields extends Contact {
  custom_fields?: {
    field: CustomField & { options?: CustomFieldOption[] };
    value?: string;
    options?: CustomFieldOption[];
  }[];
}

// Interface pour les réponses paginées
interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Interface pour les filtres avancés avec opérateur logique
interface AdvancedFilters {
  filters: Array<{
    field: string;
    operator: string;
    value: string;
  }>;
  logicOperator: 'AND' | 'OR';
}

// Interface pour les champs personnalisés
interface CustomField {
  id: number;
  name: string;
  type: 'text' | 'number' | 'date' | 'email' | 'url' | 'radio' | 'checkbox' | 'select';
  required: number; // 0 ou 1 en base de données
  color: number; // 0 ou 1 en base de données
  created_at: string;
  updated_at: string;
}

// Interface pour créer un champ personnalisé
interface CustomFieldCreateInput {
  name: string;
  type: 'text' | 'number' | 'date' | 'email' | 'url' | 'radio' | 'checkbox' | 'select';
  required: boolean;
  color?: boolean;
  options?: CustomFieldOptionInput[];
}

// Interface pour les options des champs personnalisés
interface CustomFieldOption {
  id: number;
  contact_custom_field_id: number;
  label: string;
  value?: string;
  color?: string;
  created_at: string;
  updated_at: string;
}

interface CustomFieldOptionInput {
  label: string;
  value?: string;
  color?: string;
}

// Interface pour les champs personnalisés des entreprises
interface CompanyCustomField {
  id: number;
  name: string;
  type: 'text' | 'number' | 'date' | 'email' | 'url' | 'radio' | 'checkbox' | 'select';
  required: number; // 0 ou 1 en base de données
  color: number; // 0 ou 1 en base de données
  text_case?: string;
  created_at: string;
  updated_at: string;
}

// Interface pour créer un champ personnalisé des entreprises
interface CompanyCustomFieldCreateInput {
  name: string;
  type: 'text' | 'number' | 'date' | 'email' | 'url' | 'radio' | 'checkbox' | 'select';
  required: boolean;
  color?: boolean;
  text_case?: string;
  options?: CompanyCustomFieldOptionInput[];
}

// Interface pour les options des champs personnalisés des entreprises
interface CompanyCustomFieldOption {
  id: number;
  company_custom_field_id: number;
  label: string;
  value?: string;
  color?: string;
  created_at: string;
  updated_at: string;
}

interface CompanyCustomFieldOptionInput {
  label: string;
  value?: string;
  color?: string;
}

// Interface pour les statistiques du dashboard
interface DashboardStats {
  contacts: number;
  companies: number;
  customFields: number;
}

interface StatsCount {
  count: number;
}

interface CustomFieldsStats extends StatsCount {
  breakdown: {
    contacts: number;
    companies: number;
  };
}

// Interface pour les contacts récents
interface RecentContact {
  id: number;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  company_id?: number;
  created_at: string;
  company_name?: string;
}

// Interface pour les entreprises récentes
interface RecentCompany {
  id: number;
  name: string;
  website?: string;
  city?: string;
  created_at: string;
  contacts_count: number;
}

// Settings interfaces
interface Setting {
  elementsPerPageDashboard: number;
  elementsPerPageContacts: number;
  elementsPerPageCompanies: number;
}

interface SettingInput {
  elementsPerPageDashboard: number;
  elementsPerPageContacts: number;
  elementsPerPageCompanies: number;
}

/* Clé d'API en lecture seule, telle qu'elle revient du serveur.
   La clé elle-même n'y figure jamais : la base n'en garde qu'une empreinte, et
   `key_hint` sert seulement à reconnaître une ligne dans la liste. */
/* La portée d'une clé (25/08/2026).

   `lecture` : le comportement d'origine, et le défaut.
   `suivi-up-projets` : le droit de poser la valeur d'un champ personnalisé sur
   une entreprise, et rien d'autre — ni renommer, ni créer, ni supprimer. */
export type PorteeCle = 'lecture' | 'suivi-up-projets' | 'synchro-upspot';

export interface ApiKey {
  id: number;
  label: string;
  portee: PorteeCle;
  key_hint: string;
  created_by: string | null;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

// La corbeille des contacts et des entreprises (28/10/2026)
export interface ElementCorbeille {
  id: number;
  type: 'contact' | 'entreprise';
  fiche_id: number;
  nom: string;
  detail: string | null;
  supprime_le: string;
  supprime_par: string | null;
  origine: 'interface' | 'connecteur';
}

export interface ResultatRestauration {
  type: 'contact' | 'entreprise';
  fiche_id: number;
  nom: string;
  remarques: string[];
}

// La fusion de deux fiches en double et l'alerte de doublon (28/10/2026)
export type TypeFusion = 'contact' | 'entreprise';

export interface ChampFusion {
  cle: string;
  libelle: string;
  garde: string | null;
  doublon: string | null;
  affichage_garde: string | null;
  affichage_doublon: string | null;
  etat: 'identique' | 'vide' | 'complete' | 'conflit';
}

export interface ResumeFicheFusion {
  id: number;
  nom: string;
  detail: string | null;
  compte_upspot: boolean;
  nb_contacts?: number;
}

export interface ApercuFusion {
  type: TypeFusion;
  garde: ResumeFicheFusion;
  doublon: ResumeFicheFusion;
  champs: ChampFusion[];
  blocage: string | null;
  admin_requis: boolean;
}

export interface ResultatFusion {
  garde_id: number;
  nom: string;
  contacts_deplaces: number;
  avertissements: string[];
}

export interface DoublonPossible {
  id: number;
  nom: string;
  detail: string | null;
  raison: string;
}

// Le connecteur IA (MCP) : consentement et connexions (28/10/2026)
export interface DemandeConnecteur {
  client: string;
  hote_retour: string;
  retour_local: boolean;
  lecture: boolean;
  ecriture: boolean;
  ecriture_demandee: boolean;
}

export interface ConnexionConnecteur {
  id: string;
  client: string;
  compte: string;
  depuis: string;
  ecriture: boolean;
}

export interface ClientManuelConnecteur {
  client_id: string;
  client_secret: string;
  autorisation: string;
  jeton: string;
  renouvellement: string;
  portees: string;
}

// User management interfaces
interface UserForList {
  id: string;
  username: string;
  is_admin: boolean;
  created_at: string;
  updated_at: string;
  /** Le compte n'a pas encore choisi son mot de passe (MySQL renvoie 0 ou 1). */
  invitation_en_attente?: boolean | number;
}

interface UserCreateInput {
  username: string;
  password?: string;
  is_admin: boolean;
}

interface UserUpdateInput {
  username?: string;
  is_admin?: boolean;
}

// Classe pour gérer les appels API
class ApiService {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  // Méthode générique pour faire des requêtes HTTP
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;

    // Configuration par défaut
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Ajouter le token d'authentification si disponible
    const token = this.getStoredToken();
    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      };
    }

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      // Token invalide/expiré/révoqué → redirection vers login
      if (response.status === 401) {
        localStorage.removeItem('auth-storage');
        if (navigateToLogin) navigateToLogin();
        return {
          success: false,
          error: data.error || 'Session expirée, veuillez vous reconnecter',
        };
      }

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('API Request Error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur de connexion',
      };
    }
  }

  /* Comme `request`, mais garde tout le corps d'une réponse en erreur : l'écran
     des comptes UP-SPOT a besoin du `code` (confirmation du rôle Admin, compte
     déjà existant) et des identifiants qui l'accompagnent. */
  private async requeteUpSpot<T>(endpoint: string, options: RequestInit = {}): Promise<ReponseUpSpot<T>> {
    const token = this.getStoredToken();
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...options.headers,
        },
      });
      const data = await response.json().catch(() => ({}));
      if (response.status === 401) {
        localStorage.removeItem('auth-storage');
        if (navigateToLogin) navigateToLogin();
      }
      if (!response.ok) {
        return { success: false, error: data.error || `Erreur ${response.status}`, ...data };
      }
      return data;
    } catch {
      return { success: false, error: 'Erreur de connexion' };
    }
  }

  // --- Comptes UP-SPOT ---
  accesUpSpot(contactId: number) {
    return this.requeteUpSpot<AccesUpSpotFiche>(`/api/upspot/contacts/${contactId}`);
  }
  // « Tout supprimer » depuis « Modifier » (02/10/2026) : la fiche est conservée.
  supprimerCompteUpSpot(contactId: number) {
    return this.requeteUpSpot<{ details: string }>(`/api/upspot/contacts/${contactId}/compte`, { method: 'DELETE' });
  }
  decompteReservationsUpSpot(contactId: number) {
    return this.requeteUpSpot<{ total: number; aVenir: number }>(`/api/upspot/contacts/${contactId}/reservations`);
  }
  referentielUpSpot() {
    return this.requeteUpSpot<ReferentielUpSpot>('/api/upspot/referentiel');
  }
  creerCompteUpSpot(contactId: number, donnees: {
    idRole: number; departementId: number | null;
    droits: { acces: number[]; gestion: number[] }; confirmationAdmin?: boolean;
  }) {
    return this.requeteUpSpot<CompteUpSpot>(`/api/upspot/contacts/${contactId}/compte`, {
      method: 'POST', body: JSON.stringify(donnees),
    });
  }
  modifierCompteUpSpot(contactId: number, donnees: {
    idRole?: number; departementId?: number | null; statut?: 'actif' | 'desactive'; confirmationAdmin?: boolean;
  }) {
    return this.requeteUpSpot<CompteUpSpot>(`/api/upspot/contacts/${contactId}/compte`, {
      method: 'PATCH', body: JSON.stringify(donnees),
    });
  }
  droitsUpSpot(contactId: number, droits: { acces: number[]; gestion: number[] }) {
    return this.requeteUpSpot<CompteUpSpot>(`/api/upspot/contacts/${contactId}/droits`, {
      method: 'PUT', body: JSON.stringify(droits),
    });
  }
  renvoyerInvitationUpSpot(contactId: number) {
    return this.requeteUpSpot<{ invitationEnvoyee: boolean }>(`/api/upspot/contacts/${contactId}/invitation`, {
      method: 'POST',
    });
  }
  rattacherCompteUpSpot(compteId: number, contactId: number) {
    return this.requeteUpSpot<CompteUpSpot>('/api/upspot/liens', {
      method: 'POST', body: JSON.stringify({ compteId, contactId }),
    });
  }
  etatSynchroUpSpot() {
    return this.requeteUpSpot<EtatSynchroUpSpot>('/api/upspot/synchro');
  }
  synchroniserUpSpot() {
    return this.requeteUpSpot<EtatSynchroUpSpot>('/api/upspot/synchroniser', { method: 'POST' });
  }

  // Récupérer le token depuis le localStorage
  private getStoredToken(): string | null {
    try {
      const authData = localStorage.getItem('auth-storage');
      if (authData) {
        const parsed = JSON.parse(authData);
        return parsed.state?.token || null;
      }
    } catch (error) {
      console.error('Error getting stored token:', error);
    }
    return null;
  }

  // ========== MÉTHODES D'AUTHENTIFICATION ==========

  // Connexion utilisateur
  async login(credentials: LoginCredentials): Promise<ApiResponse<AuthResponse>> {
    return this.request<AuthResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  // Récupérer le profil utilisateur actuel
  async getProfile(): Promise<ApiResponse<User>> {
    return this.request<User>('/api/auth/me');
  }

  // Déconnexion
  async logout(): Promise<ApiResponse> {
    return this.request('/api/auth/logout', {
      method: 'POST',
    });
  }

  // ========== MÉTHODES POUR LES CONTACTS ==========

  // Récupérer tous les contacts avec pagination
  async getContacts(params?: {
    page?: number;
    limit?: number;
    search?: string;
    sort?: string;
    order?: 'asc' | 'desc';
    filters?: AdvancedFilters;
    /** Fiches qui portent (« avec ») ou non (« sans ») un compte UP-SPOT. */
    upspot?: 'avec' | 'sans';
  }): Promise<ApiResponse<PaginatedResponse<Contact>>> {
    const queryString = params
      ? '?' +
        new URLSearchParams(
          Object.entries(params).reduce(
            (acc, [key, value]) => {
              if (value !== undefined && value !== null) {
                if (key === 'filters' && typeof value === 'object') {
                  // Gérer les filtres séparément en les encodant en JSON
                  acc[key] = JSON.stringify(value);
                } else {
                  acc[key] = value.toString();
                }
              }
              return acc;
            },
            {} as Record<string, string>
          )
        ).toString()
      : '';

    return this.request<PaginatedResponse<Contact>>(`/api/contacts${queryString}`);
  }

  // Récupérer un contact spécifique
  async getContact(id: number): Promise<ApiResponse<Contact>> {
    return this.request<Contact>(`/api/contacts/${id}`);
  }

  // Créer un nouveau contact
  async createContact(
    contactData: ContactCreateInputWithCustomFields
  ): Promise<ApiResponse<{ id: number }>> {
    return this.request<{ id: number }>('/api/contacts', {
      method: 'POST',
      body: JSON.stringify(contactData),
    });
  }

  // Récupérer les champs personnalisés des contacts avec comptage d'usage
  async getContactCustomFields(): Promise<
    ApiResponse<(CustomField & { usage_count: number; options?: CustomFieldOption[] })[]>
  > {
    return this.request<(CustomField & { usage_count: number; options?: CustomFieldOption[] })[]>(
      '/api/contacts/custom-fields'
    );
  }

  // Mettre à jour un contact
  async updateContact(
    id: number,
    contactData: ContactCreateInputWithCustomFields
  ): Promise<ApiResponse> {
    return this.request(`/api/contacts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(contactData),
    });
  }

  // Supprimer un contact
  /* `compteUpSpot: 'supprimer'` (01/10/2026) : la fiche part avec son compte
     UP-SPOT et toutes ses réservations. Sans, le compte est seulement désactivé. */
  async deleteContact(id: number, options: { compteUpSpot?: 'desactiver' | 'supprimer' } = {}): Promise<ApiResponse> {
    const suffixe = options.compteUpSpot === 'supprimer' ? '?compteUpSpot=supprimer' : '';
    return this.request(`/api/contacts/${id}${suffixe}`, {
      method: 'DELETE',
    });
  }

  // ---- Actions multiples sur contacts ----
  async matchContactEmails(
    emails: string[],
  ): Promise<
    ApiResponse<{
      found: { id: number; first_name: string; last_name: string; email: string }[];
      notFound: string[];
    }>
  > {
    return this.request('/api/contacts/match-emails', {
      method: 'POST',
      body: JSON.stringify({ emails }),
    });
  }

  async bulkUpdateContacts(payload: {
    ids: number[];
    standard?: Partial<Pick<Contact, 'email' | 'phone' | 'linkedin_url' | 'company_id'>>;
    custom_fields?: { id: number; value: string }[];
  }): Promise<
    ApiResponse<{
      contactsUpdated: number;
      standardFieldsApplied: boolean;
      customValuesWritten: number;
    }>
  > {
    return this.request('/api/contacts/bulk-update', {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  // ========== MÉTHODES POUR LES ENTREPRISES ==========

  // Récupérer toutes les entreprises avec pagination
  async getCompanies(params?: {
    page?: number;
    limit?: number;
    search?: string;
    sort?: string;
    order?: 'asc' | 'desc';
    filters?: AdvancedFilters;
  }): Promise<ApiResponse<PaginatedResponse<Company>>> {
    const queryString = params
      ? '?' +
        new URLSearchParams(
          Object.entries(params).reduce(
            (acc, [key, value]) => {
              if (value !== undefined && value !== null) {
                if (key === 'filters' && typeof value === 'object') {
                  // Gérer les filtres séparément en les encodant en JSON
                  acc[key] = JSON.stringify(value);
                } else {
                  acc[key] = value.toString();
                }
              }
              return acc;
            },
            {} as Record<string, string>
          )
        ).toString()
      : '';

    return this.request<PaginatedResponse<Company>>(`/api/companies${queryString}`);
  }

  // Récupérer une entreprise spécifique
  async getCompany(id: number): Promise<ApiResponse<Company>> {
    return this.request<Company>(`/api/companies/${id}`);
  }

  // Créer une nouvelle entreprise
  async createCompany(
    companyData: CompanyCreateInputWithCustomFields
  ): Promise<ApiResponse<{ id: number }>> {
    return this.request<{ id: number }>('/api/companies', {
      method: 'POST',
      body: JSON.stringify(companyData),
    });
  }

  // Récupérer les champs personnalisés des entreprises avec comptage d'usage
  async getCompanyCustomFieldsWithUsage(): Promise<
    ApiResponse<
      (CompanyCustomField & { usage_count: number; options?: CompanyCustomFieldOption[] })[]
    >
  > {
    return this.request<
      (CompanyCustomField & { usage_count: number; options?: CompanyCustomFieldOption[] })[]
    >('/api/companies/custom-fields');
  }

  // Mettre à jour une entreprise
  async updateCompany(
    id: number,
    companyData: CompanyCreateInputWithCustomFields
  ): Promise<ApiResponse> {
    return this.request(`/api/companies/${id}`, {
      method: 'PUT',
      body: JSON.stringify(companyData),
    });
  }

  // Supprimer une entreprise
  async deleteCompany(id: number): Promise<ApiResponse> {
    return this.request(`/api/companies/${id}`, {
      method: 'DELETE',
    });
  }

  // ---- Actions multiples sur entreprises ----
  async matchCompanies(queries: string[]): Promise<
    ApiResponse<{
      found: {
        id: number
        name: string
        matchedBy: 'name' | 'siret' | 'fuzzy'
        query: string
      }[]
      notFound: string[]
      suggestions: {
        query: string
        candidates: { id: number; name: string; score: number }[]
      }[]
    }>
  > {
    return this.request('/api/companies/match', {
      method: 'POST',
      body: JSON.stringify({ queries }),
    })
  }

  async bulkUpdateCompanies(payload: {
    ids: number[]
    custom_fields?: { id: number; value: string }[]
  }): Promise<
    ApiResponse<{
      companiesUpdated: number
      customValuesWritten: number
    }>
  > {
    return this.request('/api/companies/bulk-update', {
      method: 'PATCH',
      body: JSON.stringify(payload),
    })
  }

  // ========== MÉTHODES POUR LES CHAMPS PERSONNALISÉS ==========

  // Récupérer tous les champs personnalisés pour les contacts avec pagination
  async getCustomFields(params?: {
    page?: number;
    limit?: number;
    search?: string;
    sort?: string;
    order?: 'asc' | 'desc';
  }): Promise<ApiResponse<PaginatedResponse<CustomField>>> {
    const queryString = params
      ? '?' +
        new URLSearchParams(
          Object.entries(params).reduce(
            (acc, [key, value]) => {
              if (value !== undefined && value !== null) {
                acc[key] = value.toString();
              }
              return acc;
            },
            {} as Record<string, string>
          )
        ).toString()
      : '';

    return this.request<PaginatedResponse<CustomField>>(
      `/api/custom-fields/contacts${queryString}`
    );
  }

  // Récupérer un champ personnalisé spécifique
  async getCustomField(id: number): Promise<ApiResponse<CustomField>> {
    return this.request<CustomField>(`/api/custom-fields/contacts/${id}`);
  }

  // Créer un nouveau champ personnalisé
  async createCustomField(
    customFieldData: CustomFieldCreateInput
  ): Promise<ApiResponse<{ id: number }>> {
    return this.request<{ id: number }>('/api/custom-fields/contacts', {
      method: 'POST',
      body: JSON.stringify(customFieldData),
    });
  }

  // Mettre à jour un champ personnalisé
  async updateCustomField(
    id: number,
    customFieldData: Partial<CustomFieldCreateInput>
  ): Promise<ApiResponse> {
    return this.request(`/api/custom-fields/contacts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(customFieldData),
    });
  }

  // Supprimer un champ personnalisé
  async deleteCustomField(id: number): Promise<ApiResponse> {
    return this.request(`/api/custom-fields/contacts/${id}`, {
      method: 'DELETE',
    });
  }

  // ========== MÉTHODES POUR LES CHAMPS PERSONNALISÉS DES ENTREPRISES ==========

  // Récupérer tous les champs personnalisés pour les entreprises avec pagination
  async getCompanyCustomFields(params?: {
    page?: number;
    limit?: number;
    search?: string;
    sort?: string;
    order?: 'asc' | 'desc';
  }): Promise<ApiResponse<PaginatedResponse<CompanyCustomField>>> {
    const queryString = params
      ? '?' +
        new URLSearchParams(
          Object.entries(params).reduce(
            (acc, [key, value]) => {
              if (value !== undefined && value !== null) {
                acc[key] = value.toString();
              }
              return acc;
            },
            {} as Record<string, string>
          )
        ).toString()
      : '';

    return this.request<PaginatedResponse<CompanyCustomField>>(
      `/api/custom-fields/companies${queryString}`
    );
  }

  // Récupérer un champ personnalisé spécifique des entreprises
  async getCompanyCustomField(id: number): Promise<ApiResponse<CompanyCustomField>> {
    return this.request<CompanyCustomField>(`/api/custom-fields/companies/${id}`);
  }

  // Créer un nouveau champ personnalisé pour les entreprises
  async createCompanyCustomField(
    customFieldData: CompanyCustomFieldCreateInput
  ): Promise<ApiResponse<{ id: number }>> {
    return this.request<{ id: number }>('/api/custom-fields/companies', {
      method: 'POST',
      body: JSON.stringify(customFieldData),
    });
  }

  // Mettre à jour un champ personnalisé des entreprises
  async updateCompanyCustomField(
    id: number,
    customFieldData: Partial<CompanyCustomFieldCreateInput>
  ): Promise<ApiResponse> {
    return this.request(`/api/custom-fields/companies/${id}`, {
      method: 'PUT',
      body: JSON.stringify(customFieldData),
    });
  }

  // Supprimer un champ personnalisé des entreprises
  async deleteCompanyCustomField(id: number): Promise<ApiResponse> {
    return this.request(`/api/custom-fields/companies/${id}`, {
      method: 'DELETE',
    });
  }

  // ========== MÉTHODES POUR LES STATISTIQUES ==========

  // Récupérer les statistiques du dashboard
  async getDashboardStats(): Promise<ApiResponse<DashboardStats>> {
    return this.request<DashboardStats>('/api/stats/dashboard');
  }

  // Récupérer le nombre de contacts
  async getContactsCount(): Promise<ApiResponse<StatsCount>> {
    return this.request<StatsCount>('/api/stats/contacts');
  }

  // Récupérer le nombre d'entreprises
  async getCompaniesCount(): Promise<ApiResponse<StatsCount>> {
    return this.request<StatsCount>('/api/stats/companies');
  }

  // Récupérer le nombre total de champs personnalisés
  async getCustomFieldsCount(): Promise<ApiResponse<CustomFieldsStats>> {
    return this.request<CustomFieldsStats>('/api/stats/custom-fields');
  }

  // Récupérer les contacts récents
  async getRecentContacts(limit: number = 7): Promise<ApiResponse<RecentContact[]>> {
    return this.request<RecentContact[]>(`/api/stats/recent-contacts?limit=${limit}`);
  }

  // Récupérer les entreprises récentes
  async getRecentCompanies(limit: number = 7): Promise<ApiResponse<RecentCompany[]>> {
    return this.request<RecentCompany[]>(`/api/stats/recent-companies?limit=${limit}`);
  }

  // Récupérer le nombre de champs personnalisés utilisés par les contacts
  async getContactCustomFieldsUsage(): Promise<ApiResponse<CustomFieldsStats>> {
    return this.request<CustomFieldsStats>('/api/stats/custom-fields/contacts');
  }

  // Récupérer le nombre de champs personnalisés utilisés par les entreprises
  async getCompanyCustomFieldsUsage(): Promise<ApiResponse<CustomFieldsStats>> {
    return this.request<CustomFieldsStats>('/api/stats/custom-fields/companies');
  }

  // ========== MÉTHODES POUR LES PARAMÈTRES ==========

  // Récupérer tous les paramètres
  async getSettings(): Promise<ApiResponse<Setting>> {
    return this.request<Setting>('/api/settings');
  }

  // Sauvegarder les paramètres
  async saveSettings(settingsData: SettingInput): Promise<ApiResponse> {
    return this.request('/api/settings', {
      method: 'POST',
      body: JSON.stringify(settingsData),
    });
  }

  // ========== MÉTHODES POUR LA GESTION DES UTILISATEURS ==========

  // Récupérer tous les utilisateurs
  async getUsers(): Promise<ApiResponse<PaginatedResponse<UserForList>>> {
    return this.request<PaginatedResponse<UserForList>>('/api/users?limit=100');
  }

  // ── Clés d'API (lecture seule, pour les applications du parc UP) ──────────

  async getApiKeys(): Promise<ApiResponse<ApiKey[]>> {
    return this.request<ApiKey[]>('/api/api-keys');
  }

  /** La clé en clair n'est renvoyée qu'ici, et une seule fois. */
  async createApiKey(label: string, portee: PorteeCle = 'lecture'): Promise<ApiResponse<{ key: string }>> {
    return this.request<{ key: string }>('/api/api-keys', {
      method: 'POST',
      body: JSON.stringify({ label, portee }),
    });
  }

  /* Changer la portée d'une clé en circulation : ouvrir ou refermer une serrure
     sans redistribuer le secret dans toutes les applications qui l'utilisent. */
  async changerPorteeApiKey(id: number, portee: PorteeCle): Promise<ApiResponse<{ id: number; portee: PorteeCle }>> {
    return this.request<{ id: number; portee: PorteeCle }>(`/api/api-keys/${id}/portee`, {
      method: 'PATCH',
      body: JSON.stringify({ portee }),
    });
  }

  /* Remplace une clé oubliée : l'ancienne est révoquée, la nouvelle affichée
     une fois. Une clé perdue ne se retrouve pas — elle se remplace. */
  async regenerateApiKey(id: number): Promise<ApiResponse<{ key: string }>> {
    return this.request<{ key: string }>(`/api/api-keys/${id}/regenerer`, { method: 'POST' });
  }

  async revokeApiKey(id: number): Promise<ApiResponse> {
    return this.request(`/api/api-keys/${id}`, { method: 'DELETE' });
  }

  // Créer un nouvel utilisateur
  async createUser(userData: UserCreateInput): Promise<ApiResponse<{ id: string; invitationEnvoyee: boolean }>> {
    return this.request<{ id: string; invitationEnvoyee: boolean }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  // Connecteur IA
  async getDemandeConnecteur(id: string): Promise<ApiResponse<DemandeConnecteur>> {
    return this.request<DemandeConnecteur>(`/api/connecteur/demandes/${encodeURIComponent(id)}`);
  }

  async deciderDemandeConnecteur(id: string, accepter: boolean): Promise<ApiResponse<{ redirection: string }>> {
    return this.request<{ redirection: string }>(`/api/connecteur/demandes/${encodeURIComponent(id)}/decision`, {
      method: 'POST',
      body: JSON.stringify({ accepter }),
    });
  }

  async getConnexionsConnecteur(tous = false): Promise<ApiResponse<{ adresse: string; connexions: ConnexionConnecteur[] }>> {
    return this.request<{ adresse: string; connexions: ConnexionConnecteur[] }>(`/api/connecteur/connexions${tous ? '?tous=1' : ''}`);
  }

  async revoquerConnexionConnecteur(id: string): Promise<ApiResponse> {
    return this.request(`/api/connecteur/connexions/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  async creerClientConnecteur(nom: string, adresse_retour: string): Promise<ApiResponse<ClientManuelConnecteur>> {
    return this.request<ClientManuelConnecteur>('/api/connecteur/clients', {
      method: 'POST',
      body: JSON.stringify({ nom, adresse_retour }),
    });
  }

  // Fusion de deux fiches en double (28/10/2026) : `gardeId` reste, `doublonId` part à la corbeille.
  private cheminFusion(type: TypeFusion): string {
    return type === 'contact' ? '/api/contacts' : '/api/companies';
  }

  async getApercuFusion(type: TypeFusion, gardeId: number, doublonId: number): Promise<ApiResponse<ApercuFusion>> {
    return this.request<ApercuFusion>(`${this.cheminFusion(type)}/${gardeId}/fusion/${doublonId}`);
  }

  async fusionner(
    type: TypeFusion, gardeId: number, doublonId: number, choix: Record<string, 'garde' | 'doublon'>,
  ): Promise<ApiResponse<ResultatFusion>> {
    return this.request<ResultatFusion>(`${this.cheminFusion(type)}/${gardeId}/fusion`, {
      method: 'POST',
      body: JSON.stringify({ doublon_id: doublonId, choix }),
    });
  }

  // Les fiches qui ressemblent à celle qu'on saisit (alerte de doublon, sans blocage)
  async getContactsMemeEmail(email: string, sauf?: number): Promise<ApiResponse<DoublonPossible[]>> {
    const params = new URLSearchParams({ email });
    if (sauf) params.set('sauf', String(sauf));
    return this.request<DoublonPossible[]>(`/api/contacts/doublons?${params}`);
  }

  async getEntreprisesSemblables(nom: string, siret: string, sauf?: number): Promise<ApiResponse<DoublonPossible[]>> {
    const params = new URLSearchParams({ nom, siret });
    if (sauf) params.set('sauf', String(sauf));
    return this.request<DoublonPossible[]>(`/api/companies/doublons?${params}`);
  }

  // Corbeille (administrateurs)
  async getCorbeille(type?: 'contact' | 'entreprise', q?: string): Promise<ApiResponse<{ elements: ElementCorbeille[]; total: number }>> {
    const params = new URLSearchParams();
    if (type) params.set('type', type);
    if (q?.trim()) params.set('q', q.trim());
    const suite = params.toString() ? `?${params}` : '';
    return this.request<{ elements: ElementCorbeille[]; total: number }>(`/api/corbeille${suite}`);
  }

  async restaurerDeLaCorbeille(id: number): Promise<ApiResponse<ResultatRestauration>> {
    return this.request<ResultatRestauration>(`/api/corbeille/${id}/restaurer`, { method: 'POST' });
  }

  async supprimerDeLaCorbeille(id: number): Promise<ApiResponse> {
    return this.request(`/api/corbeille/${id}`, { method: 'DELETE' });
  }

  async viderLaCorbeille(): Promise<ApiResponse<{ supprimes: number }>> {
    return this.request<{ supprimes: number }>('/api/corbeille', {
      method: 'DELETE',
      body: JSON.stringify({ confirmation: 'vider' }),
    });
  }

  // Renvoyer le mail d'activation (nouveau lien de 24 heures)
  async renvoyerInvitationUtilisateur(id: string): Promise<ApiResponse<{ invitationEnvoyee: boolean }>> {
    return this.request<{ invitationEnvoyee: boolean }>(`/api/users/${id}/invitation`, { method: 'POST' });
  }

  // Mettre à jour un utilisateur
  async updateUser(id: string, userData: UserUpdateInput): Promise<ApiResponse> {
    return this.request(`/api/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  // Changer le mot de passe d'un utilisateur
  async changeUserPassword(id: string, password: string): Promise<ApiResponse> {
    return this.request(`/api/users/${id}/password`, {
      method: 'PATCH',
      body: JSON.stringify({ password }),
    });
  }

  // Supprimer un utilisateur
  async deleteUser(id: string): Promise<ApiResponse> {
    return this.request(`/api/users/${id}`, {
      method: 'DELETE',
    });
  }
}

// Instance singleton du service API
export const apiService = new ApiService(API_BASE_URL);

// Export des types pour utilisation dans d'autres fichiers
export type {
  CompteUpSpot,
  ReferentielUpSpot,
  AccesUpSpotFiche,
  EtatSynchroUpSpot,
  User,
  AuthResponse,
  LoginCredentials,
  ApiResponse,
  Company,
  CompanyCreateInput,
  CompanyCreateInputWithCustomFields,
  CompanyWithCustomFields,
  CompanyCustomValueInput,
  Contact,
  ContactCreateInput,
  ContactCreateInputWithCustomFields,
  ContactWithCustomFields,
  ContactCustomValueInput,
  CustomField,
  CustomFieldOption,
  CustomFieldCreateInput,
  CustomFieldOptionInput,
  CompanyCustomField,
  CompanyCustomFieldOption,
  CompanyCustomFieldCreateInput,
  CompanyCustomFieldOptionInput,
  PaginatedResponse,
  AdvancedFilters,
  DashboardStats,
  StatsCount,
  CustomFieldsStats,
  RecentContact,
  RecentCompany,
  Setting,
  SettingInput,
  UserForList,
  UserCreateInput,
  UserUpdateInput,
};
